import { useCallback } from 'react';
import { LineItem } from 'shared/types/wishlist/LineItem';
import { Wishlist } from 'shared/types/wishlist/Wishlist';
import useSWR, { mutate, SWRResponse } from 'swr';
import { sdk } from 'sdk';
import { revalidateOptions } from 'frontastic';
import useLocale from 'helpers/hooks/useLocale';

const useWishlist = () => {
  const { getCurrency } = useLocale();
  const extensions = sdk.composableCommerce;

  const result = useSWR('/action/wishlist/getWishlist', extensions.wishlist.getWishlist, revalidateOptions);

  const data = (result.data?.isError ? {} : { data: result.data?.data }) as SWRResponse<Wishlist>;

  const wishlistProductSKUs =
    data?.data?.lineItems
      ?.filter((item) => !item?.variant?.attributes?.isPerpetualStock) // non-perpetual product SKUs only
      ?.map((item) => item?.variant?.sku) || [];

  const totalWishlistItems = data.data?.lineItems?.reduce((acc, curr) => acc + (curr.count as number), 0) ?? 0;

  const addToWishlist = useCallback(async (wishlist: Wishlist | undefined, lineItem: LineItem, count = 1) => {
    const extensions = sdk.composableCommerce;

    const newWishlist = wishlist ? { ...wishlist, lineItems: [...(wishlist.lineItems ?? []), lineItem] } : { lineItem };

    const res = extensions.wishlist.addItem({
      variant: { sku: lineItem.variant?.sku as string, queryID: lineItem?.queryID },
      count,
    });

    await mutate('/action/wishlist/getWishlist', res, {
      optimisticData: { data: newWishlist },
      rollbackOnError: true,
    });
  }, []);

  const hasExcludedItem = !!data?.data?.lineItems?.some((item) => {
    const site = getCurrency() === 'GBP' ? 'UK' : 'EU';

    if (!item.variant?.attributes?.exclusion) {
      return false;
    }

    if (item.variant?.attributes.exclusion && item.variant?.attributes.exclusion.length > 0) {
      return item.variant?.attributes.exclusion.some((item: { key: string }) => item.key === site);
    }
    return false;
  });

  const hasSoldOutItem = !!data?.data?.lineItems?.some((item) => {
    return !item.variant?.isPerpetualStock && !item.variant?.isOnStock;
  });

  const hasDiscontinuedItem = !!data?.data?.lineItems?.some((item) => {
    return item.variant?.isDiscontinued;
  });

  const removeLineItem = useCallback(async (wishlist: Wishlist, lineItem: LineItem) => {
    const extensions = sdk.composableCommerce;

    const newWishlist = {
      ...wishlist,
      lineItems: wishlist.lineItems?.filter((item) => item.lineItemId !== lineItem.lineItemId) ?? [],
    };

    const res = extensions.wishlist.removeItem({ lineItem: { id: lineItem.lineItemId } });
    await mutate('/action/wishlist/getWishlist', res, {
      optimisticData: { data: newWishlist },
      rollbackOnError: true,
    });
  }, []);

  const deleteWishlist = useCallback(async (wishlist: Wishlist) => {
    const res = await sdk.callAction({ actionName: 'wishlist/deleteWishlist' });
    const newWishlist = {
      ...wishlist,
      lineItems: [],
    };

    if (!res.isError) {
      await mutate('/action/wishlist/getWishlist', res.data, {
        optimisticData: { data: newWishlist },
        rollbackOnError: true,
      });
    }
  }, []);

  const getWishlist = useCallback(async (wishlistId: string) => {
    const extenstions = sdk.composableCommerce;

    const res = await extenstions.wishlist.queryWishlist({ wishlistId });

    return (res.isError ? {} : { data: res.data }) as SWRResponse<Wishlist>;
  }, []);

  const updateLineItem = useCallback(async (wishlist: Wishlist, lineItem: LineItem, count = 1) => {
    const extensions = sdk.composableCommerce;

    const newWishlist = {
      ...wishlist,
      lineItems:
        wishlist.lineItems?.map((item) => {
          if (item.lineItemId === lineItem.lineItemId) {
            return { ...lineItem, count: ++count };
          }
        }) ?? [],
    };

    const res = await extensions.wishlist.updateItem({ lineItem: { id: lineItem.lineItemId }, count });

    if (!res.isError) {
      await mutate('/action/wishlist/getWishlist', res.data, {
        optimisticData: newWishlist,
        rollbackOnError: true,
      });
    }
  }, []);

  return {
    ...data,
    totalItems: totalWishlistItems,
    addToWishlist,
    removeLineItem,
    deleteWishlist,
    updateLineItem,
    getWishlist,
    wishlistProductSKUs,
    hasExcludedItem,
    hasSoldOutItem,
    hasDiscontinuedItem,
  };
};

export default useWishlist;
